import React, {useState, useEffect, useRef} from 'react';
import cx from 'classnames';
import {useTranslations, createTranslator} from 'next-intl';
import {Grid, Row, Col} from '@/grid';
import withMainLayout from 'layouts/main';
import {SectionPaddingMdToZero} from '@/section';
import Container from '@/container';
import Link from '@/links';
import {withBoldWeight} from '@/hocs/withFontType';
import {H2, P, PMarginSm, PNoMargin, H4} from '@/typography';
import {ListNumbered} from '@/ui/lists/list-icons';
import {
  withMarginTopDoubleLg,
  withMarginTopZeroToMd,
  withMarginBottomMd,
  withMarginBottomXl,
  withMarginLeftXs,
  withMarginBottomXxl
} from '@/hocs/withSpacing';
import {withVisibleDesktop993, withHiddenDesktop993} from '@/hocs/withResponsiveUtils';
import TermsForm from '@/ui/hubspotform/terms/terms-form';
import Breadcrumbs from '@/ui/breadcrumbs';
import LegalHero from '@/content/legal/hero';
import useTermsTabItems from '@/ui/tabs/tab-items/terms-tabs';
import legal from 'pages/legal/legal.module.scss';
import {parseLegalRichText} from '@/ui/legal/legal-rich-text';
import {pageMessagesProps} from 'helpers/i18n';
import {
  handlePrint,
  scrollToTopOfPage,
  scrollableDropdownMob
} from '../components/ui/legal/legal.helper';
import shared from '../messages/en/shared.json';
import legalMessages from '../messages/en/legal.json';
import terms from '../messages/en/terms.json';

const seoMetas = props => {
  const seoT = createTranslator({locale: props.locale, messages: props.messages});
  return {
    title: seoT('terms.seo.title'),
    description: seoT('terms.seo.description'),
    canonical: seoT('terms.seo.canonical')
  };
};

const ListOl = props => <ListNumbered {...props} />;

const BoldSpan = withBoldWeight('span');
const DivMarginedLeftXl = withMarginLeftXs('div');
const PriorVersionMargin = withMarginTopDoubleLg('div');
const MarginedH2 = withMarginTopZeroToMd(withMarginBottomMd(H2));
const DivMarginedBottomXl = withMarginBottomXl('div');
const BreadcrumbsDesktop = withVisibleDesktop993(withMarginBottomXxl('div'));
const HiddenInDesktop = withHiddenDesktop993('div');

const TermsPage = () => {
  const dropdownRef = useRef();
  // eslint-disable-next-line no-unused-vars
  const [activeTab, setActiveTab] = useState('general-terms');
  // eslint-disable-next-line no-unused-vars
  const [isDropdownFixed, setIsDropdownFixed] = useState(false);
  // These states are crucial for the component's functionality:
  // - `setActiveTab` is used to manage the active tab state, which is essential for the Breadcrumbs component to display the correct tab.
  // - `setIsDropdownFixed` is used in conjunction with the scrollableDropdownMob function to fix the dropdown position on scroll, ensuring proper layout and user interaction.
  const t = useTranslations('terms');
  const legalT = useTranslations('legal');
  const tabItems = useTermsTabItems();
  const tableOfContentsGeneral = t.raw('tableOfContents');

  const renderItem = item => {
    if (typeof item === 'string') {
      return <li key={item}>{parseLegalRichText(item)}</li>;
    }
    return (
      <li key={item.text}>
        {parseLegalRichText(item.text)}
        <ListOl>
          {item.subItems.map(sub => (
            <li key={sub}>{parseLegalRichText(sub)}</li>
          ))}
        </ListOl>
      </li>
    );
  };

  const GeneralTermsContent = () => (
    <div>
      <MarginedH2>{t('heading')}</MarginedH2>
      <DivMarginedBottomXl>
        <Link className="no-print" onClick={handlePrint} variant="secondary-no-gradient-download">
          {t('downloadLabel')}
        </Link>
      </DivMarginedBottomXl>
      <div className={cx(legal['table-of-contents'], 'no-print')}>
        <ol>
          {tableOfContentsGeneral.map((item, index) => (
            <li key={index}>
              <Link href={item.urlCopy} variant="green800">
                {item.title}
              </Link>
            </li>
          ))}
        </ol>
      </div>
      {t.raw('introRich').map((paragraph, index) => {
        const ParagraphTag = index === 0 ? PMarginSm : P;
        return <ParagraphTag key={paragraph}>{parseLegalRichText(paragraph)}</ParagraphTag>;
      })}

      <ListOl className={legal['scroll-mg-t']}>
        {t.raw('sections').map(section => (
          <li id={section.id} key={section.id}>
            <H4>{section.title}</H4>
            <ListOl>{section.items.map(renderItem)}</ListOl>
          </li>
        ))}
        <li id="definitions">
          <H4>{t('definitionsHeading')}</H4>
          <PMarginSm>{t('definitionsIntro')}</PMarginSm>
          {t.raw('definitionsBlocks').map(block => (
            <PMarginSm key={block}>{parseLegalRichText(block)}</PMarginSm>
          ))}
        </li>
        <PMarginSm>{t('definitionsClosing')}</PMarginSm>
      </ListOl>
      <DivMarginedLeftXl>
        <PriorVersionMargin className={legal['legal-content']}>
          <PMarginSm className="heading-6">
            <BoldSpan>{t('lastUpdated')}</BoldSpan>
          </PMarginSm>
          <PNoMargin>
            <BoldSpan>{t('archivedVersionsLabel')}</BoldSpan>
          </PNoMargin>
          {t.raw('archivedVersions').map(version => (
            <PNoMargin key={version.href}>
              <Link href={version.href} target="_blank" variant="default">
                {version.text}
              </Link>
            </PNoMargin>
          ))}
        </PriorVersionMargin>
      </DivMarginedLeftXl>
    </div>
  );

  useEffect(() => {
    scrollableDropdownMob(dropdownRef, setIsDropdownFixed);
  }, []);

  useEffect(() => {
    window.addEventListener('afterprint', () => {
      window.location.reload();
    });
  }, []);

  return (
    <>
      <LegalHero title={t('heading')} />
      <SectionPaddingMdToZero>
        <Container>
          <Grid fluid>
            <BreadcrumbsDesktop>
              <Breadcrumbs
                clickableLink={{text: legalT('breadcrumb.legal'), urlCopy: '/legal'}}
                title={t('heading')}
                activeTab={tabItems.find(item => item.value === activeTab).text}
              />
            </BreadcrumbsDesktop>
            <Row>
              <Col xs={12} md={12} lg={9} lgOffset={3}>
                <GeneralTermsContent />
              </Col>
            </Row>
            <HiddenInDesktop>
              <Link variant="go-to-top-arrow" onClick={scrollToTopOfPage} />
            </HiddenInDesktop>
            <TermsForm />
          </Grid>
        </Container>
      </SectionPaddingMdToZero>
    </>
  );
};

export default withMainLayout(TermsPage, seoMetas);

export const messagesForIntl = {shared, legal: legalMessages, terms};

export function getStaticProps() {
  return pageMessagesProps(messagesForIntl);
}
